<template>
  <div class="transaction-container" :id="predictedTransaction.envelopeId" :class="{'editable': canEditEnvelope}"
       v-on:[editTransactionEvent]="openEditPredictedTransaction">
    <div class="transaction-row">
      <div class="transaction-name">
        <span class="edit-container" :class="{'wiggle': shouldWiggle}" v-if="canEditEnvelope">
          <img class="edit" :src="require('@/assets/pencil.svg')" />
        </span>
        <span class="name">{{ predictedTransaction.name }}</span>
      </div>
    </div>
    <div class="amounts-row">
      <div class="amount actual-amount">{{ formatAmountWithFractionAndCurrency(actualAmount, 1) }}</div>
      <div v-if="showPrediction" class="amount">{{ formatAmountWithFractionAndCurrency(predictedTransaction.predictedAmount, 1) }}</div>
    </div>
    <div class="more-details" v-if="hasActual">
      <img class="icon" :src="require('@/assets/subdirectory-arrow-left@3x.png')">
      <span>{{ formatDate(predictedTransaction.actual.transactionDate) }} </span>
      <span class="transaction-name">{{ predictedTransaction.actual.businessName }} </span>
      <span>{{ getTransactionPayment(predictedTransaction.actual) }}</span>
      <span>{{ getTransactionAccount(predictedTransaction.actual) }}</span>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import TransactionDisplayUtils from '../../TransactionDisplayUtils.vue';
import Segment from '../../../../../../../../../Segment';
import ModalBus from '../../../../../../../../../event-bus/modal-bus';
import EditPredictedWithActualFlow
  from '../../../current-month/components/cashflow-edit/edit-transaction/edit-predicted-transaction/flows/EditPredictedWithActualFlow.vue';
import EditPredictedWOActualFlow
  from '../../../current-month/components/cashflow-edit/edit-transaction/edit-predicted-transaction/flows/EditPredictedWOActualFlow.vue';
import EditPredictedIncomeWOActualFlow
  from '../../../current-month/components/cashflow-edit/edit-transaction/edit-predicted-transaction/flows/EditPredictedIncomeWOActualFlow.vue';

export default {
  name: 'PredictedTransactionRow',
  mixins: [TransactionDisplayUtils],
  props: {
    predictedTransaction: {
      type: Object,
      required: true,
    },
    canEditCategory: {
      type: Boolean,
      required: false,
      default: true,
    },
    showPrediction: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  created() {
    if (this.shouldWiggle) {
      localStorage.setItem('riseup.editWasViewed', 'true');
    }
  },
  computed: {
    hasActual() {
      return this.predictedTransaction.actual;
    },
    canCancelSequence() {
      return this.canEditCategory && !this.hasActual && this.isIncome;
    },
    shouldWiggle() {
      const editWasViewed = localStorage.getItem('riseup.editWasViewed');
      return !editWasViewed;
    },
    canEditEnvelope() {
      if (this.isIncome) {
        return this.canEditCategory && !this.hasActual;
      }
      return this.canEditCategory;
    },
    editTransactionEvent() {
      return this.canEditEnvelope ? 'click' : null;
    },
    actualAmount() {
      if (this.hasActual) {
        // TODO yack!
        return this.predictedTransaction.actual.billingAmount ?
          this.predictedTransaction.actual.billingAmount :
          this.predictedTransaction.actual.incomeAmount;
      }
      return 0;
    },
    isIncome() {
      return this.predictedTransaction.isIncome;
    },
    editTransactionPopupToShow() {
      if (this.isIncome) {
        return EditPredictedIncomeWOActualFlow;
      }
      return this.hasActual ? EditPredictedWithActualFlow : EditPredictedWOActualFlow;
    },
  },
  methods: {
    ...mapActions('editPredictedTransaction', ['startEditingPredictedTransaction']),
    openEditPredictedTransaction() {
      Segment.trackUserInteraction('EditPredictedTransactionClicked', { businessName: this.predictedTransaction.name });
      const popupToOpen = this.editTransactionPopupToShow;
      ModalBus.$emit('open', {
        component: popupToOpen,
        props: {
          predictedTransaction: this.predictedTransaction,
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
  @import '../../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';

  .transaction-container {
    --pencil-bg-color: $riseup_white_1;
    padding: 8px 10px 4px 4px;
    background-color: $riseup_gray_0;
    border-bottom: 1px solid $riseup_white;
    transition: background-color 1s ease;

    &.editable {
      cursor: pointer;
    }

    &:first-of-type {
      box-shadow: inset 0 2px 3px 0 rgba(155, 155, 155, 0.51);
    }

    &:last-of-type {
      border-bottom: none;
    }

    &.highlight {
      background-color: $riseup_gray_1;
    }

    .transaction-row {
      display: flex;
      justify-content: space-between;
      height: 15px;
      font-size: 10px;
      line-height: 1;

      .transaction-name {
        display: flex;

        .edit-container {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          background-color: var(--pencil-bg-color);
          margin-top: -2px;
          margin-left: 7px;

          &.wiggle {
            animation: fadein 1.5s ease-out;
            animation-iteration-count: 3;

            @keyframes fadein {
              50% {
                opacity: 0;
              }
            }
          }

          .edit {
            margin-right: 3px;
            margin-top: 3px;
            width: 10px;
            height: 10px;
          }
        }

        .name {
          max-width: 230px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

    }

    .amounts-row {
      display: flex;
      padding-right: 90px;
      height: 14px;
      line-height: 14px;
      font-size: 10px;

      .amount {
        width: 88px;
      }

      .actual-amount {
        font-weight: bold;
      }
    }

    .more-details {
      display: flex;
      white-space: nowrap;
      height: 14px;
      font-size: 10px;
      line-height: 1;
      color: $riseup_gray_3;
      margin-top: 4px;

      span {
        overflow: hidden;
        padding-left: 2px;
      }

      .transaction-name {
        max-width: 126px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      img {
        margin-top: 2px;
        height: 6px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/generic-components/cashflow-types/cashflow-types-base/PredictedTransacionRow.vue